import { useEffect, useState } from 'react'
import { Button, Card, Container, Form, Modal, Spinner } from 'react-bootstrap'
import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { observer } from 'mobx-react-lite'
import FooterNew from '../components/Footer'
import { useStore } from '../../app/stores/store'
import { history } from '../..'
import ErrorMessage from '../components/ErrorMessage'
import ResendConfirmEmail from './ResendConfirmEmail'
import ForgetPassword from './ForgetPassword'
import PageLoader from '../components/loaders/PageLoader'

function Login() {
  const [user, setUser] = useState({
    email: '',
    password: '',
  })
  const [showPassword, setShowPassword] = useState(false)
  const { userStore } = useStore()
  const [showRes, setShowRes] = useState(false)
  const [loadingBtn, setLoadingBtn] = useState(false)
  const [error, setError] = useState('')
  const { login, setRegisterEmail, toggleResendConfirmEmail } = userStore
  const { t } = useTranslation()
  const scrollToTop = () => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
  }
  const location = useLocation()
  const params = new URLSearchParams(location.search)
  const auctionId = params.get('auctionId')
  const redirectTo = params.get('redirectTo')
  const token = params.get('token')
  const validateEmail = params.get('validateEmail')

  const queryParams = {
    auctionId: auctionId!,
    token: token!,
    validateEmail: validateEmail!,
  }

  useEffect(() => {
    scrollToTop()
  }, [])

  async function handleLogin(e: any) {
    e.preventDefault()
    setLoadingBtn(true)
    await login(user)
      .then(() => {
        setError('')
        if(redirectTo){
          history.push(redirectTo)
        }
        else if (auctionId) {
          history.push({
            pathname: `/auctions/${auctionId}`,
            search: new URLSearchParams(queryParams).toString(),
          })
        }
      })
      .catch((error) => {
        setLoadingBtn(false)
        setError(error.response.data.errors)
      })
      .finally(() => setLoadingBtn(false))
  }

  function handleInputChangeLogin(e: any) {
    const { name, value } = e.target
    setUser({ ...user, [name]: value })
  }

  function handleResendConfirm() {
    setRegisterEmail(user.email)
    toggleResendConfirmEmail(true)
  }

  function close() {
    setShowRes(false)
  }

  const formData = [
    {
      label: t('email'),
      type: 'email',
      name: 'email',
      placeholder: 'name@example.com',
      value: user.email,
      required: true,
    },
    {
      label: t('password'),
      type: showPassword ? 'text' : 'password',
      name: 'password',
      placeholder: '********',
      value: user.password,
      required: true,
    },
  ]

  const registerData = [
    {
      title: t('register_yourself'),
      onClick: () => setError(''),
      to: auctionId
        ? {
            pathname: '/register',
            search: new URLSearchParams(queryParams).toString(),
          }
        : '/register',
    },
  ]

  if (userStore.loadingGoogle) return <PageLoader />

  return (
    <>
      <Container fluid className='card-center card-background'>
        <Card className='newFont prevent-select card-shadow login-card-size'>
          <Card.Header className='bg-white'>
            <h1 className='login-card-title'>{t('login_header')}</h1>
          </Card.Header>
          <Card.Body className='card-padding'>
            <Form onSubmit={handleLogin}>
              {formData.map(({ label, type, name, placeholder, value, required }, index) => (
                <Form.Group key={index} className='mb-3 card-font-size'>
                  <Form.Label className='login-p'>{label}</Form.Label>
                  <Form.Control
                    className='card-input-style'
                    type={type}
                    name={name}
                    placeholder={placeholder}
                    onChange={handleInputChangeLogin}
                    value={value}
                    required={required}
                  />
                </Form.Group>
              ))}
              <Form.Group className='d-flex align-items-center login-media-center text-black pt-3 card-font-size'>
                <label
                  onClick={() => setShowPassword(!showPassword)}
                  className='d-flex gap-2 align-items-center'
                >
                  <span
                    className={`text-black mb-0 pointer-cursor ${
                      showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'
                    }`}
                  />
                  {t('show_password')}
                </label>
                <Form.Label
                  onClick={() => {
                    setShowRes(true)
                    setError('')
                  }}
                  className='mb-0 pointer-cursor text-center card-passwordUnderline'
                >
                  {t('forgot_password')}?
                </Form.Label>
              </Form.Group>

              {error === 'email_not_confirmed' ? (
                <div>
                  <p className='mt-4'>
                    <span onClick={handleResendConfirm} className='confirmEmail'>
                      {t('confrim_email')}!
                    </span>
                    <ErrorMessage text={t(error)} setError={() => setError('')} />
                  </p>
                </div>
              ) : (
                error && <ErrorMessage text={t(error)} setError={() => setError('')} />
              )}

              <Button
                type='submit'
                variant='light'
                className='mt-4 card-login-button'
                disabled={loadingBtn}
              >
                {loadingBtn ? <Spinner animation='border' variant='light' /> : t('log_in')}
              </Button>

              <div className='mt-4'>
                <div className='row justify-content-center align-items-center'>
                  <div className='line-register col' />
                  <p className='col-5 text-center m-0'>{t('login_social')}</p>
                  <div className='line-register col' />
                </div>
                <div className='d-flex justify-content-center mt-4 gap-4'>
                  <Button className='google-icon-login' onClick={userStore.googleLogin}>
                    <i className='fa-brands fa-google' />
                  </Button>
                  {/* <Button
                    onClick={userStore.facebookLogin}
                    className="google-icon-login"
                  >
                    <i className="fa-brands fa-facebook"></i>
                  </Button> */}
                </div>
              </div>

              <Form.Group
                style={{ paddingTop: '25px' }}
                className='d-flex text-center justify-content-center align-items-center gap-2 text-black card-font-size'
              >
                <Form.Label>{t('no_acc_yet')}</Form.Label>

                <Form.Label className='card-passwordUnderline text-center greenColor'>
                  {registerData.map((data, index) => (
                    <Link key={index} onClick={data.onClick} to={data.to}>
                      {data.title}
                    </Link>
                  ))}
                </Form.Label>
              </Form.Group>
            </Form>
          </Card.Body>
        </Card>
      </Container>
      <ResendConfirmEmail />
      <Modal
        show={showRes}
        onHide={() => {
          setShowRes(false)
          setError('')
        }}
        className='forget-password-modal'
        centered
      >
        <ForgetPassword close={close} />
      </Modal>
      <FooterNew />
    </>
  )
}
export default observer(Login)
